import Eras from '@/roc/era';
import validateBuildingPlacements from '@/roc/url/BuildingPlacementValidation';
import Buildings from '@/roc/building';
import {getBuildingTypeName} from '@/roc/building/BuildingType';
import Grid from '@/roc/grid/Grid';
import Wonders from "@/roc/wonders";

const showParseError = function (e) {
  if (process.env.NODE_ENV === 'production') {
    e = '';
  }
  alert('Given layout could not be loaded due to errors in its format ' + (e ? '(' + e + ')' : ''));
};

const decodeBuilding = function (params) {
  const items = params.split('.');
  if (items.length < 6) {
    throw new Error('Invalid building params: ' + params);
  }

  let era = 'bronzeAge';
  const wonderIdToWonderMap = {};

  for (let i = 0; i < Wonders.length; i++) {
    wonderIdToWonderMap[Wonders[i].id.toString()] = Wonders[i];
  }

  const id = items[0];
  const tileX = parseInt(items[1]);
  const tileY = parseInt(items[2]);
  const rotate = parseInt(items[3]);
  const level = parseInt(items[4]);

  let bIdValid = false;
  let bLevelValid = false;
  let template = null;
  let isWonder = false;

  if (wonderIdToWonderMap[id] === undefined) {
    for (let i = 0; i < Buildings.length; i++) {
      if (Buildings[i].id === id) {
        bIdValid = true;
        template = Buildings[i];

        if (level >= Buildings[i].minLevel && level <= Buildings[i].maxLevel) {
          bLevelValid = true;
        }
        break;
      }
    }
  } else {
    bIdValid = true;
    isWonder = true;
    template = wonderIdToWonderMap[id];

    if (level >= wonderIdToWonderMap[id].minLevel && level <= wonderIdToWonderMap[id].maxLevel) {
      bLevelValid = true;
    }
  }

  if (!bIdValid || template === null) {
    throw new Error('Invalid id: ' + id);
  } else if (!bLevelValid) {
    throw new Error('Invalid level: ' + level + ' for building ' + getBuildingTypeName(template.type) + ' id: ' + id);
  } else if (rotate !== 0 && rotate !== 1) {
    throw new Error('Invalid rotate: ' + rotate);
  }

  if (isWonder) {
    return {
      id,
      level,
      active: false
    };
  }

  if (tileX < 0 || tileX > Grid.CELL_COUNT_X - 1) {
    throw new Error('Invalid tileX: ' + tileX);
  }

  if (tileY < 0 || tileY > Grid.CELL_COUNT_Y - 1) {
    throw new Error('Invalid tileY: ' + tileY);
  }

  if (parseInt(items[5]) !== 0) {
    let bEraValid = false;

    for (const i in Eras) {
      if (Eras[i].id === items[5]) {
        era = i;
        bEraValid = true;
        break;
      }
    }

    if (!bEraValid) {
      throw new Error('Invalid building era: ' + items[5]);
    }
  }

  return {
    id,
    tileX,
    tileY,
    rotate: rotate === 1,
    level,
    era,
  };
};

const decodeExpansions = function (data) {
  const parts = data.split(':');
  const result = [];

  if (parts.length > 1) {
    for (let i = 1; i < parts.length; i++) {
      result.push({
        x: parseInt(parts[i].split(',')[0]),
        y: parseInt(parts[i].split(',')[1])
      });
    }
  }

  return result;
};

const decodeWonders = function (data) {
  const parts = data.split(':');
  const result = [];

  if (parts.length > 1) {
    for (let i = 1; i < parts.length; i++) {
      result.push({
        id: parts[i].split(',')[0],
        level: parseInt(parts[i].split(',')[1]),
        active: parseInt(parts[i].split(',')[2]) === 1
      });
    }
  }

  return result;
};

const decodeArtifacts = function (data) {
  const parts = data.split(':');
  const result = [];

  if (parts.length > 1) {
    for (let i = 1; i < parts.length; i++) {
      result.push({
        id: parts[i].split(',')[0],
        active: parseInt(parts[i].split(',')[1]) === 1
      });
    }
  }

  return result;
};

const decodePlainURL = function (params) {
  if (params.length <= 1) {
    showParseError('Not enough params');
    return null;
  }

  const result = {
    era: '',
    buildings: [],
    expansions: [],
    wonders: [],
    artifacts: [],
    grid: {
      x: null,
      y: null
    }
  };

  let eraFound = false;
  const header = params[0];
  let eraId;

  if (header.indexOf('.') !== -1) {
    const parts = header.split('.');
    eraId = parts[0];
    result.grid.x = parseInt(parts[1]);
    result.grid.y = parseInt(parts[2]);
  } else {
    eraId = header;
  }

  for (const i in Eras) {
    if (Eras[i].id === eraId) {
      result.era = i;
      eraFound = true;
      break;
    }
  }

  if (!eraFound) {
    showParseError('Era not found');
    return null;
  }

  for (let i = 1; i < params.length; i++) {
    try {
      if (params[i].indexOf('e') === 0) {
        result.expansions.push(
          ...decodeExpansions(params[i])
        );
      } else if (params[i].indexOf('w') === 0) {
        result.wonders.push(
          ...decodeWonders(params[i])
        );
      } else if (params[i].indexOf('a') === 0) {
        result.artifacts.push(
          ...decodeArtifacts(params[i])
        );
      } else {
        const b = decodeBuilding(params[i]);
        if (b.active !== undefined) {
          result.wonders.push(b);
        } else {
          result.buildings.push(b);
        }
      }
    } catch (e) {
      showParseError(e.message);
      return null;
    }
  }

  try {
    validateBuildingPlacements(result.buildings, result.era);
  } catch (e) {
    showParseError(e.message);
    return null;
  }

  return result;
};

export default {
  decodePlainURL
};
